import { Component, type ReactNode } from 'react';
import { Warning, ArrowCounterClockwise, House, ArrowLeft } from '@phosphor-icons/react';
import { reportClientError } from '@/lib/errorReporter';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Production-grade Error Boundary
 * Catches JavaScript errors anywhere in the child component tree.
 *
 * IMPORTANT: Always mount with key={location.pathname} in AppShell so the
 * boundary resets automatically on every route change. Without this, a crash
 * on one page freezes navigation for the rest of the session.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    // Report the crash SHAPE to the platform diagnostics console (content-free, best-effort).
    reportClientError({ source: 'react', name: error.name, message: error.message, stack: error.stack });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center shadow-lg">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/15">
              <Warning className="h-8 w-8 text-red-600 dark:text-red-400" weight="fill" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-foreground">Something went wrong</h1>
            <p className="mb-6 text-sm text-muted-foreground">An unexpected error occurred.</p>

            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 rounded-lg bg-muted/50 p-4 text-left">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Error</p>
                <p className="text-xs text-red-600 dark:text-red-400">{this.state.error.message}</p>
                {this.state.errorInfo && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">Stack</summary>
                    <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap text-[10px] text-muted-foreground">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-700"
              >
                <ArrowCounterClockwise className="h-4 w-4" />
                Try again
              </button>
              <button
                onClick={() => { window.location.href = '/'; }}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-muted"
              >
                <House className="h-4 w-4" />
                Go home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Page-level error fallback — shown inside the content area.
 * Navigation (sidebar, topbar) remains functional because the ErrorBoundary
 * only wraps <Outlet>, not the whole shell.
 *
 * With key={location.pathname} on the boundary, clicking any sidebar link
 * resets this automatically — no manual retry needed.
 */
export function PageErrorFallback({ error }: { error?: Error; resetError?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Warning className="h-5 w-5 text-muted-foreground" weight="duotone" />
      </div>
      <h2 className="mb-1.5 text-base font-semibold text-foreground">Failed to load this page</h2>
      <p className="mb-5 max-w-xs text-sm text-muted-foreground">
        {error?.message || 'An error occurred. Use the sidebar to navigate to another page.'}
      </p>
      <button
        onClick={() => window.history.back()}
        className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
      >
        <ArrowLeft className="h-4 w-4" />
        Go back
      </button>
      {import.meta.env.DEV && error && (
        <p className="mt-4 max-w-sm text-[10px] text-muted-foreground/60">{error.message}</p>
      )}
    </div>
  );
}
